import { CardCarousel as SharedCardCarousel } from 'components/shared/LazyClientComponents';

import IconVariant from './variants/IconVariant';
import ImageVariant from './variants/ImageVariant';
import TextVariant from './variants/TextVariant';
import { VideoVariant } from 'components/shared/LazyClientComponents';

import { getColorwayName } from 'utils/formatters';
import { cn } from 'utils/styles';
import { getEntryNativeFields } from 'utils/entryNativeFields';
import { getModuleSectionName } from 'utils/modules';
import { Margins } from 'types/constants';

import { liveUpdateAttrs } from 'libs/contentful/utils';

import { resolveCards } from './toCards';
import {
  CardCarouselProps,
  CardCarouselClassNames,
  CardStyle,
  CardVariant,
  CardVariantProps,
  CardCarouselEntryFields,
} from './types';

const CardCarousel = (props: CardCarouselProps) => {
  const fields = getEntryNativeFields<CardCarouselEntryFields>(props);

  const color = getColorwayName(props.color ?? fields.color);
  const margins = props.margins ?? fields.margins ?? Margins.Centered;
  const cardStyle = fields.cardStyle ?? CardStyle.None;
  const sectionName = getModuleSectionName(props);
  const { className, paginationPortalRef, getCarouselPaginationThreshold } = props;

  const internalClassNames: CardCarouselClassNames = {
    carousel: cn(className?.carousel),
    pagination: cn(className?.pagination),
    cardGrid: cn(className?.cardGrid),
    wrapper: cn(className?.wrapper),
    iconVariant: className?.iconVariant ?? {},
    imageVariant: className?.imageVariant ?? {},
    textVariant: className?.textVariant ?? {},
    videoVariant: className?.videoVariant ?? {},
  };

  const getCardVariant = (cardProps: CardVariantProps) => {
    switch (cardProps.variant) {
      case CardVariant.Icon:
        return (
          <IconVariant
            {...cardProps}
            cardStyle={cardStyle}
            key={cardProps.sys?.id}
            className={internalClassNames.iconVariant}
          />
        );
      case CardVariant.Text:
        return (
          <TextVariant
            {...cardProps}
            cardStyle={cardStyle}
            key={cardProps.sys?.id}
            className={internalClassNames.textVariant}
          />
        );
      case CardVariant.Video:
        return (
          <VideoVariant
            {...cardProps}
            cardStyle={cardStyle}
            key={cardProps.sys?.id}
            className={internalClassNames.videoVariant}
          />
        );
      default:
        return (
          <ImageVariant
            {...cardProps}
            cardStyle={cardStyle}
            key={cardProps.sys?.id}
            className={internalClassNames.imageVariant}
          />
        );
    }
  };

  const items = resolveCards(fields.cards);
  if (!items.length) return null;

  return (
    <section
      data-testid="section"
      {...(sectionName != null && { id: encodeURIComponent(sectionName) })}
      {...liveUpdateAttrs(props.sys?.id, 'cardCarousel')}>
      <SharedCardCarousel
        totalItems={items.length}
        color={color}
        margins={margins}
        sectionName={sectionName}
        carouselClassName={internalClassNames.carousel}
        paginationClassName={internalClassNames.pagination}
        cardGridClassName={internalClassNames.cardGrid}
        paginationPortalRef={paginationPortalRef}
        getCarouselPaginationThreshold={getCarouselPaginationThreshold}>
        {items.map((card, index) => {
          return getCardVariant({
            ...card,
            index,
            total: items.length,
          });
        })}
      </SharedCardCarousel>
    </section>
  );
};

export default CardCarousel;
